import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AiSidebar         from "./AiSidebar";
import AiChatBox         from "./AiChatBox";
import AuthRequiredGate  from "./AuthRequiredGate";
import ConfirmDialog     from "./Confirmdialog";
import ShareChatDialog   from "./ShareChatDialog";
import CompareBar        from "./CompareBar";
import CompareModal      from "./CompareModal";
import Icon              from "./Icon";

import useKeyboardShortcuts from "./Usekeyboardshortcuts";
import useGeolocation, { detectNearMeIntent } from "./Usegeolocation";

import LocationPermissionModal from "./LocationPermissionModal";

import { uid, isAutoTitle, isTrivialTitle, PLACEHOLDER_TITLE, titleForFirstMessage, withDisclosures } from "./Helpers";
import { detectRouteIntent }             from "./RouteHelper";
import {
    ENDPOINTS,
    KEYBOARD_SHORTCUTS,
    TALK_TO_ME_ENTRY_POINT,
    GUIDED_ONBOARDING_SEED_QUESTION,
    onboardingGateMessage,
} from "./Constants";

import { sessionStorageService, AUTH_ROUTES, authenticationApi, SESSION_EXPIRED_EVENT } from "../../features/authentication";
import { PROFILE_UPDATED_EVENT } from "./SettingsModal";

import "../../styles/ai/ai-variables.css";
import "../../styles/ai/ai-layout.css";

/**
 * Main AI property search page: owns chat session state (create/rename/
 * delete/persist), routes user messages to the AI/agent backend with
 * location and follow-up context, and hosts the compare-properties flow.
 */
export default function AiSearchPage() {
    const location        = useLocation();
    const navigate        = useNavigate();
    const initialQuestion = location.state?.question;
    const fromProperty    = location.state?.fromSlug || null;
    // Set when the visitor arrived via the homepage "Talk to Me" pill
    // (directly, or back through the login redirect). The pill carries no
    // state of its own -- this page stays the owner of the chat session and
    // of the guided flow; the flag only says which way in was used.
    const isGuidedEntry   = location.state?.entryPoint === TALK_TO_ME_ENTRY_POINT;

    const [chats,        setChats]        = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [loading,      setLoading]      = useState(false);
    const [sidebarOpen,  setSidebarOpen]  = useState(() => window.innerWidth > 640);
    // Set only when the initial chat-history load fails (network blip, auth
    // hiccup, server error) -- surfaced in the sidebar with a retry button
    // instead of silently falling through to an empty "New Chat" state,
    // which used to look indistinguishable from a genuinely new account.
    const [historyError, setHistoryError] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(true);

    const [compareList,  setCompareList]  = useState([]);
    const [compareOpen,  setCompareOpen]  = useState(false);

    // Whether there's a session at all. Read once at mount — if it changes
    // mid-session (token expires), sendMessage's 401 handling below takes
    // over rather than this recomputing.
    const isAuthenticated = useMemo(() => !!sessionStorageService.get(), []);

    // Must read the token via sessionStorageService (the same key used
    // everywhere else in the app) rather than any other localStorage key,
    // or logged-in users fall through to the Guest default. The JWT only
    // encodes email (no name claim), so this decode is just a placeholder
    // shown until GET /api/auth/me resolves below and supplies the real name.
    const [currentUser, setCurrentUser] = useState(() => {
        try {
            const token = sessionStorageService.get();
            if (token) {
                const payload = JSON.parse(atob(token.split(".")[1]));
                const email = payload.email || payload.sub || "";
                return {
                    name:     email.split("@")[0] || "User",
                    email,
                    role:     "Member",
                    avatar:   null,
                    initials: (email || "U").charAt(0).toUpperCase(),
                };
            }
        } catch { /* silent */ }
        return { name: "Guest", email: "", role: "Free Plan", avatar: null, initials: "G" };
    });

    useEffect(() => {
        if (!isAuthenticated) return;
        authenticationApi.getCurrentUser()
            .then((data) => {
                if (!data?.fullName) return;
                setCurrentUser((prev) => ({
                    ...prev,
                    name: data.fullName,
                    email: data.email || prev.email,
                    avatar: data.avatarUrl || prev.avatar,
                    initials: data.fullName.charAt(0).toUpperCase(),
                }));
            })
            .catch(() => { /* keep the JWT-derived placeholder */ });
    }, [isAuthenticated]);

    useEffect(() => {
        const onProfileUpdated = ({ detail }) => setCurrentUser((prev) => ({
            ...prev,
            ...(detail.fullName ? { name: detail.fullName, initials: detail.fullName.charAt(0).toUpperCase() } : {}),
            ...(detail.avatarUrl ? { avatar: detail.avatarUrl } : {}),
        }));
        window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
        return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    }, []);

    const [deleteTarget,      setDeleteTarget]      = useState(null);
    const [shareTarget,       setShareTarget]       = useState(null);
    const [toast,             setToast]             = useState({ visible: false, text: "" });
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [pendingQuery,      setPendingQuery]       = useState("");

    const {
        position, locationName, isGranted, isDenied,
    } = useGeolocation();

    const hasSentInitial         = useRef(false);
    const hasStartedGuidedEntry  = useRef(false);
    const chatsRef               = useRef([]);
    // Closes a real race the `loading` state alone can't: state updates
    // aren't visible until React re-renders, so several synchronous calls
    // into sendMessage (e.g. a Retry button clicked more than once before
    // the render carrying loading=true commits) all see the same stale
    // loading=false and each append their own duplicate user message +
    // request. A ref mutates immediately, closing the gap. Found live:
    // 3 rapid Retry clicks produced 3 duplicate user bubbles and 3 AI
    // responses to the same message.
    const sendingRef              = useRef(false);

    useEffect(() => { chatsRef.current = chats; }, [chats]);

    const activeChat = useMemo(
        () => chats.find((c) => c.id === activeChatId) || null,
        [chats, activeChatId]
    );

    const makeChat = useCallback((title = "New Chat") => ({
        id: uid(), title, messages: [], createdAt: Date.now(),
    }), []);

    // In-flight first saves per chat id -- a second autosave landing before
    // the first returns its backendId must update that row, not create another.
    const pendingCreatesRef = useRef(new Map());

    /**
     * Persists a chat to the backend, tied to the logged-in account.
     *
     * IMPORTANT: chat.id is a stable, client-only identifier and must NEVER
     * be reassigned to the backend's row id. sendMessage captures
     * activeChatId in a closure at the start of an async round-trip and uses
     * it to append the AI's reply when the response lands; if chat.id were
     * rewritten in the meantime (e.g. by the debounced autosave below
     * finishing first), that reply would silently fail to match any chat.
     * The backend id is tracked separately as chat.backendId instead, used
     * only for update/delete calls.
     */
    const saveChatToBackend = useCallback(async (chat) => {
        if (!chat || chat.messages.length === 0) return;
        if (!sessionStorageService.get()) return;

        const firstUserMsg = chat.messages.find((m) => m.role === "user");
        const payload = {
            title: (chat.title && chat.title !== "New Chat" ? chat.title : firstUserMsg?.text?.slice(0, 40))
                || "AI Search Chat",
            preview: firstUserMsg?.text?.slice(0, 60) || "",
            messages: chat.messages,
            // Lets loadHistory() resume this exact chat_cache.py key after a
            // reload instead of falling back to `backend-${id}` -- see
            // loadHistory's own comment and 0015_chat_sessions_client_chat_id.
            clientChatId: chat.id,
        };

        try {
            const pending = pendingCreatesRef.current.get(chat.id);
            const backendId = chat.backendId || (pending ? await pending : null);
            if (backendId) {
                await authenticationApi.updateChatSession(backendId, payload);
            } else {
                const create = authenticationApi.saveChatSession(payload).then((saved) => saved.id);
                pendingCreatesRef.current.set(chat.id, create);
                create.catch(() => pendingCreatesRef.current.delete(chat.id));
                const savedId = await create;
                setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, backendId: savedId } : c)));
            }
        } catch {
            // Best-effort - local chat state is unaffected either way.
        }
    }, []);

    const showToast = useCallback((text) => {
        setToast({ visible: true, text });
        setTimeout(() => setToast({ visible: false, text: "" }), 2500);
    }, []);

    const toggleCompare = useCallback((property) => {
        setCompareList((prev) => {
            const exists = prev.find((p) => p.id === property.id);
            if (exists) {
                showToast(`"${property.title}" removed from compare`);
                return prev.filter((p) => p.id !== property.id);
            }
            if (prev.length >= 4) {
                showToast("You can compare up to 4 properties at a time");
                return prev;
            }
            showToast(`"${property.title}" added to compare`);
            return [...prev, property];
        });
    }, [showToast]);

    const clearCompare = useCallback(() => {
        setCompareList([]);
        setCompareOpen(false);
    }, []);

    const createNewChat = useCallback(() => {
        if (activeChat) saveChatToBackend(activeChat);
        const chat = makeChat();
        setChats((prev) => [chat, ...prev]);
        setActiveChatId(chat.id);
    }, [makeChat, activeChat, saveChatToBackend]);

    const renameChat = useCallback((chatId, newTitle) => {
        setChats((prev) => {
            const next = prev.map((c) => (c.id === chatId ? { ...c, title: newTitle, titleLocked: true } : c));
            const renamed = next.find((c) => c.id === chatId);
            // Only persists once the chat has at least one message -
            // saveChatToBackend no-ops on empty chats, same as autosave.
            if (renamed) saveChatToBackend(renamed);
            return next;
        });
    }, [saveChatToBackend]);

    const downloadChatPdf = useCallback(async (chat) => {
        if (!chat?.backendId) return;
        try {
            const { blob, filename } = await authenticationApi.downloadChatSessionPdf(chat.backendId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            showToast("Couldn't download the chat PDF. Please try again.");
        }
    }, [showToast]);

    // A chat started this session may not have its first save back yet: save it now rather than ignore the tap.
    const ensureSaved = useCallback(async (chat) => {
        if (chat?.backendId) return chat.backendId;
        if (!chat?.messages?.length) return null;
        if (!pendingCreatesRef.current.has(chat.id)) saveChatToBackend(chat);
        return (await pendingCreatesRef.current.get(chat.id)?.catch(() => null)) || null;
    }, [saveChatToBackend]);

    const pinChat = useCallback(async (chat, pinned) => {
        const setPinned = (value) =>
            setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, pinned: value } : c)));
        setPinned(pinned);
        try {
            const backendId = await ensureSaved(chat);
            if (!backendId) throw new Error("not saved");
            await authenticationApi.pinChatSession(backendId, pinned);
        } catch {
            setPinned(!pinned);
            showToast(pinned ? "Couldn't pin the chat. Please try again." : "Couldn't unpin the chat. Please try again.");
        }
    }, [showToast, ensureSaved]);

    const setChatShareToken = useCallback((chatId, shareToken) => {
        setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, shareToken } : c)));
    }, []);

    const confirmDeleteChat = useCallback(() => {
        if (!deleteTarget) return;

        const target = chatsRef.current.find((c) => c.id === deleteTarget);
        if (target?.backendId && sessionStorageService.get()) {
            authenticationApi.deleteChatSession(target.backendId).catch(() => {});
        }

        setChats((prev) => {
            const next = prev.filter((c) => c.id !== deleteTarget);
            if (activeChatId === deleteTarget) {
                if (next.length > 0) {
                    setActiveChatId(next[0].id);
                } else {
                    const fresh = makeChat();
                    next.push(fresh);
                    setActiveChatId(fresh.id);
                }
            }
            return next;
        });
        setDeleteTarget(null);
    }, [deleteTarget, activeChatId, makeChat]);

    const appendMessage = useCallback((chatId, msg) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: [...chat.messages, { ...msg, timestamp: Date.now() }] }
                    : chat
            )
        );
    }, []);

    const editMessage = useCallback((chatId, msgId, newText) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: chat.messages.map((m) =>
                            m.id === msgId ? { ...m, text: newText, edited: true } : m) }
                    : chat
            )
        );
    }, []);

    const copyMessageText = useCallback((text) => {
        navigator.clipboard?.writeText(text);
        showToast("Copied to clipboard");
    }, [showToast]);

    const setMessageRelaxationApplied = useCallback((chatId, msgId, applied) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: chat.messages.map((m) =>
                            m.id === msgId ? { ...m, relaxationApplied: applied } : m) }
                    : chat
            )
        );
    }, []);

    // Turns a relaxation chip from informational into an explicit,
    // user-chosen action -- never applied automatically. Mirrors
    // GuidedSearch.jsx's own applyRelaxation (same backend endpoint,
    // same "append user + ai turn" shape) -- this was the one surface
    // that never got it wired up, so every relaxation chip in the main
    // Ask AI chat rendered permanently disabled.
    const updateMessage = useCallback((chatId, msgId, fields) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: chat.messages.map((m) =>
                            m.id === msgId ? { ...m, ...(typeof fields === "function" ? fields(m) : fields) } : m) }
                    : chat
            )
        );
    }, []);

    // "Load more" reads the next page of the search's frozen order -- nothing is re-ranked, so pages never reshuffle.
    const loadMoreResults = useCallback(async (msg) => {
        const chatId = activeChatId;
        const token = sessionStorageService.get();
        if (!chatId || !token || !msg?.planId || msg.nextCursor == null) return;

        updateMessage(chatId, msg.id, { loadingMore: true });
        try {
            const response = await fetch(ENDPOINTS.AI_MORE, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ chatId, planId: msg.planId, cursor: msg.nextCursor }),
            });
            if (response.status === 410) {
                updateMessage(chatId, msg.id, { loadingMore: false, nextCursor: null });
                showToast("These results have expired. Please run the search again.");
                return;
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            updateMessage(chatId, msg.id, (m) => {
                const seen = new Set((m.properties || []).map((p) => p.id));
                return {
                    loadingMore: false,
                    properties: [...(m.properties || []), ...(data.properties || []).filter((p) => !seen.has(p.id))],
                    nextCursor: data.nextCursor ?? null,
                };
            });
        } catch (err) {
            updateMessage(chatId, msg.id, { loadingMore: false });
            showToast("Couldn't load more results. Please try again.");
        }
    }, [activeChatId, updateMessage, showToast]);

    const applyRelaxation = useCallback(async (dimension, label, sourceMessageId) => {
        const chatId = activeChatId;
        const token = sessionStorageService.get();
        if (!chatId || !token) return;

        setMessageRelaxationApplied(chatId, sourceMessageId, dimension);
        appendMessage(chatId, { id: uid(), role: "user", text: label });

        try {
            const response = await fetch(ENDPOINTS.PERSONAL_AGENT_APPLY_RELAXATION, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ chatId, dimension }),
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            const result = data.results || {};
            appendMessage(chatId, {
                id: uid(),
                role: "ai",
                text: result.message || "",
                hasResults: result.hasResults || false,
                properties: result.properties || [],
                followUps: result.followUps || [],
                relaxationOptions: result.relaxationOptions || [],
            });
        } catch (err) {
            showToast("Couldn't apply that change. Please try again.");
            setMessageRelaxationApplied(chatId, sourceMessageId, null);
        }
    }, [activeChatId, appendMessage, setMessageRelaxationApplied, showToast]);

    const sendMessage = useCallback(
        async (input) => {
            const isObject = input !== null && typeof input === "object";
            const text     = isObject ? input.question?.trim() : input?.trim();
            let userLat    = isObject ? input.userLatitude     : null;
            let userLng    = isObject ? input.userLongitude    : null;
            let userLocName = isObject ? input.userLocationName : null;
            // Skips rendering a user bubble and the title rename below --
            // used only for the "Talk to Me" guided-entry seed question,
            // which exists purely to trigger the backend's onboarding flow
            // and was never meant to be shown as something the visitor typed.
            const silent    = isObject ? !!input.silent : false;
            // Reply to a stage-2 map confirmation (AiMessage.js) -- `text`
            // is the confirmed/corrected place name itself, not a new
            // free-text question. See ENDPOINTS.AI_ASK body below and
            // orchestrator/nodes/search.py's _continue_inline_onboarding.
            const confirmPendingLocation = isObject ? !!input.confirmPendingLocation : false;
            // The stage-2 area-shape editor's finalized circles (up to 5,
            // each {name, lat, lng, radiusKm}) -- sent alongside
            // confirmPendingLocation so the backend uses these exact
            // (possibly dragged/resized) values instead of re-geocoding
            // `text`. See orchestrator/nodes/search.py's
            // _continue_inline_onboarding confirm branch.
            const confirmedAreaShapes = isObject ? (input.confirmedAreaShapes || null) : null;
            // Reply to the post-onboarding priority-picker card (AiMessage.js) --
            // `text` is a placeholder label, not a new free-text query.
            // requirementPriorities is the actual tap-to-select payload: row id
            // -> "high"|"med"|"low", one entry per non-fixed row the user
            // assigned. See orchestrator/nodes/search.py's
            // _continue_inline_onboarding for how this triggers the search the
            // card was gating.
            const confirmPriorities = isObject ? !!input.confirmPriorities : false;
            const requirementPriorities = isObject ? (input.requirementPriorities || null) : null;
            // Level -> metric row ids, only where the user arranged two or more distance wants ("Which should decide first?").
            const metricOrder = isObject ? (input.metricOrder || null) : null;

            if (!text || !activeChatId || loading || sendingRef.current) return;
            sendingRef.current = true;

            // Safety net: the AuthRequiredGate branch below already stops
            // an unauthenticated person from ever reaching this component's
            // chat UI, so this only fires if a token that was present at
            // mount gets cleared out from under an active session.
            const token = sessionStorageService.get();
            if (!token) {
                sendingRef.current = false;
                navigate(AUTH_ROUTES.LOGIN, {
                    state: { redirectTo: "/ai-search", pendingQuery: text },
                });
                return;
            }

            const needsLocation = detectNearMeIntent(text);
            const isLocationAgentQuery = /tell me about|how is the area|commute|drive time|travel time|what.*near|schools near|hospital near|compare.*area|rental yield|investment potential|infrastructure|neighborhood/i.test(text);

            const kmChipMatch = text.match(/within\s+\d+\s*km\s+of\s+(.+)/i);
            let explicitLocationRequested = false;

            if (kmChipMatch && kmChipMatch[1]) {
                userLocName = kmChipMatch[1].trim();
                userLat = null;
                userLng = null;
                explicitLocationRequested = true;
            }

            if (!explicitLocationRequested) {
                if (!userLat && isLocationAgentQuery && position) {
                    userLat     = position.latitude;
                    userLng     = position.longitude;
                    userLocName = userLocName ?? position.locationName ?? locationName ?? null;
                }

                if (needsLocation && !userLat) {
                    if (!isDenied && isGranted && position) {
                        userLat = position.latitude;
                        userLng = position.longitude;
                    } else if (!isDenied) {
                        sendingRef.current = false;
                        setPendingQuery(text);
                        setLocationModalOpen(true);
                        return;
                    }
                }

                if (!userLat && position) {
                    userLat     = position.latitude;
                    userLng     = position.longitude;
                    userLocName = userLocName ?? position.locationName ?? locationName ?? null;
                }
            }

            const chatId = activeChatId;
            // The greeting a new chat opens on (AiChatBox) becomes part of the chat, and this first message is its answer.
            const onboardingGateShown = !silent
                && (chatsRef.current.find((c) => c.id === chatId)?.messages || []).length === 0;
            if (onboardingGateShown) appendMessage(chatId, { ...onboardingGateMessage(), id: uid() });
            if (!silent) {
                appendMessage(chatId, { id: uid(), role: "user", text });

                setChats((prev) =>
                    prev.map((chat) =>
                        chat.id === chatId && chat.title === "New Chat"
                            ? { ...chat, title: titleForFirstMessage(text) }
                            : chat
                    )
                );
            }

            let apiQuestion = text;
            const msgs = chatsRef.current.find(c => c.id === chatId)?.messages || [];
            const lastAi = [...msgs].reverse().find(m => m.role === "ai" && m.properties?.length > 0);

            if (lastAi && lastAi.properties) {
                const props = lastAi.properties;
                if (props[0] && props[0].location) {
                    apiQuestion = apiQuestion.replace(/(1\s*st|first|above|this)\s+(property|project|home|apartment|villa|building)/gi, props[0].location);
                }
                if (props[1] && props[1].location) {
                    apiQuestion = apiQuestion.replace(/(2\s*nd|second)\s+(property|project|home|apartment|villa|building)/gi, props[1].location);
                }
            }

            let isRouteQuery = !!detectRouteIntent(apiQuestion);
            let routeInfo    = isRouteQuery ? detectRouteIntent(apiQuestion) : null;

            if (routeInfo?.usePropAsOrigin) {
                for (let i = msgs.length - 1; i >= 0; i--) {
                    const m = msgs[i];
                    if (m.role === "ai" && m.properties?.length > 0) {
                        const p    = m.properties[0];
                        const pLat = parseFloat(p.latitude ?? p.lat);
                        const pLng = parseFloat(p.longitude ?? p.lng);
                        if (!isNaN(pLat) && !isNaN(pLng)) {
                            routeInfo.origin      = { lat: pLat, lng: pLng };
                            routeInfo.originLabel = p.location || p.title;
                        } else if (p.location) {
                            routeInfo.origin      = p.location;
                            routeInfo.originLabel = p.location || p.title;
                        }
                        break;
                    }
                }
            }

            setLoading(true);

            try {
                const response = await fetch(ENDPOINTS.AI_ASK, {
                    method:  "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization:  `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        question:         apiQuestion,
                        chatId,
                        userLatitude:     userLat  ?? null,
                        userLongitude:    userLng  ?? null,
                        userLocationName: userLocName ?? locationName ?? null,
                        isRouteQuery,
                        confirmPendingLocation,
                        confirmedAreaShapes,
                        confirmPriorities,
                        requirementPriorities,
                        metricOrder,
                        onboardingGateShown,
                    }),
                });

                if (response.status === 401) {
                    // Token expired/invalid mid-session — send them to log
                    // back in rather than showing a generic error message.
                    navigate(AUTH_ROUTES.LOGIN, {
                        state: { redirectTo: "/ai-search", pendingQuery: text },
                    });
                    return;
                }

                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json();

                const isLocationAgent = data.isLocationAgent === true;
                // "walking distance to a metro" reads like a route request, but when the backend answered with
                // its own turn (an onboarding question, the priority picker, ranked results) that answer wins.
                const backendOwnsTurn = data.isInlineOnboarding === true || data.isPriorityPicker === true
                    || !!data.evaluationPlanId;
                if (isLocationAgent || backendOwnsTurn) {
                    isRouteQuery = false;
                    routeInfo = null;
                }

                let fetchedProperties = data.properties || [];

                const routeOriginLabel = routeInfo?.originLabel || (
                    typeof routeInfo?.origin === "object" ? "This Location" : routeInfo?.origin || "Unknown"
                );

                const routeText = isRouteQuery
                    ? `🗺️ Showing route from **${routeOriginLabel}** to **${routeInfo.destination}** on the map.\n\nUse the travel mode tabs to switch modes.`
                    : withDisclosures(data.message || "No response received.", data.requirementDisclosures, data.excludedByFixed, data.eligibleCount);

                appendMessage(chatId, {
                    id:           uid(),
                    role:         "ai",
                    text:         routeText,
                    isRouteQuery,
                    isLocationAgent,
                    isRoadSearch: data.isRoadSearch === true,
                    roadName:     data.roadName || null,
                    roadPolyline: data.roadPolyline || null,
                    bufferKm:     data.bufferKm ?? null,
                    hasResults:   isRouteQuery ? false : (fetchedProperties.length > 0),
                    properties:   isRouteQuery ? []    : fetchedProperties,
                    followUps:    isRouteQuery ? []    : (data.followUps   || []),
                    // Found live: never captured from the /ask response at
                    // all in this flow (only GuidedSearch.jsx's own separate
                    // fetch calls read it) -- a zero-result reply's
                    // relaxation chips (AiMessage.js) had nothing to render
                    // regardless of onApplyRelaxation being wired up or not.
                    relaxationOptions: isRouteQuery ? [] : (data.relaxationOptions || []),
                    // Ranking v1: the search's frozen order is paged with "Load more" (planId + nextCursor).
                    planId:        isRouteQuery ? null : (data.evaluationPlanId || null),
                    nextCursor:    isRouteQuery ? null : (data.nextCursor ?? null),
                    orderedCount:  isRouteQuery ? null : (data.orderedCount ?? null),
                    // Inline onboarding (personal_agent's 6-question flow asked
                    // turn-by-turn in this same chat): stage 1/4's answers have
                    // a fixed set, surfaced as clickable chips under the
                    // question instead of a typed reply. isInlineOnboarding
                    // hides Copy/Retry on these messages (AiMessage.js) -- a
                    // fixed onboarding question isn't generated content worth
                    // copying or regenerating.
                    onboardingOptions:      isRouteQuery ? null  : (data.onboardingOptions || null),
                    // Property-type stage only -- lets AiMessage.js toggle
                    // multiple chips (e.g. "Villas" + "Apartments") before
                    // submitting, instead of a single chip click submitting
                    // immediately (every other select-type stage's behavior).
                    onboardingMultiSelect:  isRouteQuery ? false : (data.onboardingMultiSelect === true),
                    // Stage-2 (location) confirmation map -- see
                    // AiMessage.js's rendering of these two fields together.
                    onboardingAreaPins:          isRouteQuery ? null  : (data.onboardingAreaPins || null),
                    onboardingAwaitingConfirmation: isRouteQuery ? false : (data.onboardingAwaitingConfirmation === true),
                    // Post-onboarding requirement-priority card (Q1-6
                    // complete) -- see AiMessage.js's rendering of these
                    // two fields together.
                    isPriorityPicker:       isRouteQuery ? false : (data.isPriorityPicker === true),
                    priorityPickerItems:    isRouteQuery ? null  : (data.priorityPickerItems || null),
                    isInlineOnboarding:     isRouteQuery ? false : (data.isInlineOnboarding === true),
                    onboardingStageIndex:   isRouteQuery ? null  : (data.onboardingStageIndex ?? null),
                    onboardingTotalStages:  isRouteQuery ? null  : (data.onboardingTotalStages ?? null),
                    // A comparison response's markdown (CONFIRMED PROPERTY
                    // DATA / USER REQUIREMENT MATCH / AI RECOMMENDATION,
                    // each with real field-by-field bullet lists) is the
                    // actual content of the message, not filler -- unlike a
                    // default property-search reply, which is intentionally
                    // one plain sentence with the cards doing the rest of
                    // the talking. Without keepIntroList, msg-text-intro's
                    // `ul, ol { display: none }` (ai-chatbox.css) silently
                    // hides every comparison field, leaving only the bare
                    // section headers on screen. Found live: a real
                    // "compare the first and second" reply rendered with
                    // zero visible content under any of its three headings.
                    keepIntroList: isRouteQuery ? false : !!data.comparison,
                    chatTitle:     isRouteQuery ? null  : (data.chatTitle || null),
                });

                if (!isRouteQuery && data.chatTitle) {
                    setChats((prev) => prev.map((c) => (
                        c.id === chatId && !c.titleLocked ? { ...c, title: data.chatTitle } : c
                    )));
                }
            } catch (err) {
                console.error("[AiSearchPage] sendMessage error:", err);

                let errorMsg = "Something went wrong. Please try again.";

                if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
                    errorMsg = "Network connection issue. Please check your internet and try again.";
                } else if (err.message?.includes("timeout") || err.message?.includes("timed out")) {
                    errorMsg = "Request timed out. The server might be busy. Please try again in a moment.";
                } else if (err.message?.includes("HTTP 429")) {
                    errorMsg = "Too many requests. Please wait a moment before trying again.";
                } else if (err.message?.includes("HTTP 500") || err.message?.includes("HTTP 502") || err.message?.includes("HTTP 503")) {
                    errorMsg = "Server error. Our team has been notified. Please try again in a few minutes.";
                } else if (err.message?.includes("HTTP 401") || err.message?.includes("HTTP 403")) {
                    errorMsg = "Authentication error. Please log in again.";
                } else if (err.message) {
                    errorMsg = `Error: ${err.message}. Please try again.`;
                }

                appendMessage(chatId, {
                    id: uid(), role: "ai",
                    text: errorMsg,
                    hasResults: false, properties: [], isError: true,
                });
            } finally {
                setLoading(false);
                sendingRef.current = false;
            }
        },
        [activeChatId, loading, appendMessage, isGranted, isDenied, position, locationName, navigate]
    );

    const handleLocationGranted = useCallback((pos) => {
        if (pendingQuery) {
            sendMessage({
                question:         pendingQuery,
                userLatitude:     pos.latitude,
                userLongitude:    pos.longitude,
                userLocationName: pos.locationName ?? null,
            });
            setPendingQuery("");
        }
    }, [pendingQuery, sendMessage]);

    const retryMessage = useCallback((chatId, msgIndex) => {
        // sendMessage's own `loading` guard only protects once its delayed
        // call actually runs -- a second Retry click landing before that
        // delay elapses (very plausible; nothing disabled the button in
        // between) truncated the chat and scheduled its own duplicate
        // sendMessage independently, since neither click had made it far
        // enough yet to see `loading` as true. Found live: rapid retries on
        // the same message appended the same user bubble 2-3 times, each
        // getting a separate AI response. Checked synchronously, before
        // scheduling anything, so a second click while one is already
        // in flight is rejected outright rather than racing it.
        if (loading) return;
        const chat = chatsRef.current.find((c) => c.id === chatId);
        if (!chat) return;
        const userMsg = chat.messages.slice(0, msgIndex).reverse().find((m) => m.role === "user");
        if (userMsg) {
            setChats((prev) =>
                prev.map((c) =>
                    c.id === chatId ? { ...c, messages: c.messages.slice(0, msgIndex) } : c
                )
            );
            // setTimeout, not requestAnimationFrame -- rAF callbacks are
            // paused entirely by the browser while the tab is hidden/
            // unfocused (backgrounded, minimized, another tab active).
            // Found live: retrying, then switching away from the tab
            // before the double-rAF delay elapsed, left the retry silently
            // never sending at all -- no request, no error, no response,
            // just the truncated message with nothing to replace it.
            // setTimeout still fires in a background tab (Chrome throttles
            // it, doesn't pause it), so the retry actually completes
            // whether or not this tab has focus when it fires.
            setTimeout(() => {
                sendMessage(userMsg.text);
            }, 0);
        }
    }, [sendMessage, loading]);

    // The stored login was refused while on this page: send the user to log in and bring them back here.
    useEffect(() => {
        const onExpired = () => navigate(AUTH_ROUTES.LOGIN, { state: { redirectTo: "/ai-search" } });
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, [navigate]);

    const loadHistory = useCallback(() => {
        setHistoryError(null);
        setHistoryLoading(true);
        authenticationApi.listChatHistory()
            .then((sessions) => {
                // Prefer s.clientChatId -- the same uuid the live session
                // was using when it got saved (see saveChatToBackend's
                // payload) -- so chat_cache.py's in-memory, chat_id-keyed
                // ConversationContext (inline onboarding stage, hard_filter,
                // etc.) is still found after a reload, not just the message
                // history (which is persisted regardless and would display
                // full progress either way).
                //
                // `backend-${s.id}` is a fallback for chats saved before
                // clientChatId existed. It's stable (derived from the
                // backend's own row id, NOT a fresh uid() per load) so at
                // least it doesn't fragment further across reloads, but it's
                // still a key chat_cache has never seen for that chat's
                // *original* live session -- found live: every reload sent
                // the server a chatId it had never seen before, so the very
                // next answer restarted the 6-question flow from stage 1
                // with no indication anything had gone wrong. clientChatId
                // closes that gap for every chat saved from now on; old
                // rows keep the old (imperfect but no-worse) behavior.
                const loaded = (sessions || []).map((s) => ({
                    id: s.clientChatId || `backend-${s.id}`,
                    backendId: s.id,
                    title: isTrivialTitle(s.title) ? PLACEHOLDER_TITLE : s.title,
                    titleLocked: !isAutoTitle(s.title, s.messages || []),
                    messages: s.messages || [],
                    createdAt: new Date(s.updatedAt || s.createdAt).getTime(),
                    pinned: Boolean(s.pinned),
                    shareToken: s.shareToken || null,
                }));
                const fresh = makeChat();
                setChats([fresh, ...loaded]);
                setActiveChatId(fresh.id);
            })
            .catch((err) => {
                console.error("[AiSearchPage] Failed to load chat history:", err);
                setHistoryError(
                    err?.message?.includes("fetch") || err?.message?.includes("NetworkError")
                        ? "Couldn't reach the server. Check your connection and try again."
                        : "Something went wrong loading your chat history."
                );
                createNewChat();
            })
            .finally(() => setHistoryLoading(false));
    }, [createNewChat, makeChat]);

    const didInit = useRef(false);
    useEffect(() => {
        if (!isAuthenticated) return;
        if (didInit.current) return;
        didInit.current = true;
        loadHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Debounced auto-save: this page has no explicit "close chat" action,
    // so the active chat is saved a little after each change instead -
    // saveChatToBackend itself decides create-vs-update based on chat.id.
    useEffect(() => {
        if (!isAuthenticated || !activeChat || activeChat.messages.length === 0) return;
        const timer = setTimeout(() => saveChatToBackend(activeChat), 1500);
        return () => clearTimeout(timer);
    }, [activeChat, isAuthenticated, saveChatToBackend]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (initialQuestion && activeChatId && !hasSentInitial.current) {
            hasSentInitial.current = true;
            sendMessage(initialQuestion);
        }
    }, [initialQuestion, activeChatId, sendMessage, isAuthenticated]);

    // "Talk to Me" entry: hand straight over to the EXISTING guided
    // onboarding by posting a deliberately vague opening turn.
    //
    // The welcome message and the Yes/No gate ahead of the 6 questions are
    // NOT rendered here -- they come back as the actual /ask response below
    // (backend's search.py::_start_inline_onboarding), same as every other
    // onboarding-stage reply. This needed to move server-side once the gate
    // started applying to organic vague typed messages too, not just this
    // pill entry -- the frontend has no way to inject an intro for a trigger
    // it never sees happen. All this effect does is post the opening turn --
    // the chat id, the history persistence and the flow itself stay exactly
    // where they were.
    useEffect(() => {
        if (!isAuthenticated || !isGuidedEntry) return;
        if (!activeChatId || hasStartedGuidedEntry.current) return;
        hasStartedGuidedEntry.current = true;
        // An empty chat already shows the greeting; only a chat with history needs the seed turn.
        if ((chatsRef.current.find((c) => c.id === activeChatId)?.messages || []).length === 0) return;
        sendMessage({ question: GUIDED_ONBOARDING_SEED_QUESTION, silent: true });
    }, [isGuidedEntry, isAuthenticated, activeChatId, sendMessage]);

    useKeyboardShortcuts(useMemo(() => ({
        [KEYBOARD_SHORTCUTS.NEW_CHAT]:       createNewChat,
        [KEYBOARD_SHORTCUTS.TOGGLE_SIDEBAR]: () => setSidebarOpen((v) => !v),
    }), [createNewChat]));

    // Not logged in: show an in-page "sign in to continue" card instead of
    // bouncing straight to the login form with no context. This intentionally
    // sits after every hook above so hook order stays stable across renders.
    if (!isAuthenticated) {
        return (
            <div className="ai-layout">
                <AuthRequiredGate
                    pendingQuery={initialQuestion}
                    redirectTo="/ai-search"
                    entryPoint={location.state?.entryPoint ?? null}
                />
            </div>
        );
    }

    return (
        <div className="ai-layout">

            <LocationPermissionModal
                open={locationModalOpen}
                onClose={() => { setLocationModalOpen(false); setPendingQuery(""); }}
                onLocationGranted={handleLocationGranted}
                queryText={pendingQuery}
            />

            {sidebarOpen && (
                <div
                    className="ai-sidebar-backdrop"
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            <AiSidebar
                chats={chats}
                activeChatId={activeChatId}
                setActiveChatId={setActiveChatId}
                createNewChat={createNewChat}
                onRenameChat={renameChat}
                onDownloadChat={downloadChatPdf}
                onDeleteChat={(chat) => setDeleteTarget(chat.id)}
                onPinChat={pinChat}
                onShareChat={(chat) => setShareTarget(chat.id)}
                collapsed={!sidebarOpen}
                user={currentUser}
                historyError={historyError}
                historyLoading={historyLoading}
                onRetryHistory={loadHistory}
            />

            <main className="ai-main">
                <div className="ai-topbar">
                    <button className="topbar-btn"
                            onClick={() => setSidebarOpen((v) => !v)}
                            title={sidebarOpen ? "Hide sidebar (Ctrl+B)" : "Show sidebar (Ctrl+B)"}
                            aria-label="Toggle sidebar">
                        <Icon name="sidebar" size={16} />
                    </button>

                    <div
                        className="topbar-logo"
                        onClick={() => fromProperty ? navigate(`/property/${fromProperty}`) : navigate("/")}
                        title={fromProperty ? "Back to property" : "Go to homepage"}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === "Enter" && (fromProperty ? navigate(`/property/${fromProperty}`) : navigate("/"))}
                        aria-label={fromProperty ? "Back to property" : "Go to homepage"}
                    >
                        <img src="/logo.png" alt="OGM" className="topbar-logo-img" />
                        <div className="topbar-logo-text">
                            <span className="topbar-logo-name">One Global Marketplace</span>
                        </div>
                    </div>

                    <span className="topbar-title">{activeChat?.title || "New Chat"}</span>
                    <div className="topbar-spacer" />
                </div>

                <AiChatBox
                    chat={activeChat}
                    loading={loading}
                    onSend={sendMessage}
                    onEditMessage={editMessage}
                    onRetry={retryMessage}
                    onCopy={copyMessageText}
                    onApplyRelaxation={applyRelaxation}
                    onLoadMore={loadMoreResults}
                    userPosition={position}
                    compareList={compareList}
                    onCompare={toggleCompare}
                />
            </main>

            <div className={`toast ${toast.visible ? "visible" : ""}`}
                 role="status" aria-live="polite">
                {toast.text}
            </div>

            <CompareBar
                count={compareList.length}
                properties={compareList}
                onClick={() => setCompareOpen(true)}
                onClear={clearCompare}
            />

            {compareOpen && (
                <CompareModal
                    properties={compareList}
                    onClose={() => setCompareOpen(false)}
                    onRemove={(id) => setCompareList((prev) => prev.filter((p) => p.id !== id))}
                />
            )}

            {deleteTarget && (
                <ConfirmDialog
                    title="Delete Chat"
                    message={chats.find((c) => c.id === deleteTarget)?.shareToken
                        ? "This conversation and all its messages will be permanently removed, and its share link will stop working."
                        : "This conversation and all its messages will be permanently removed."}
                    confirmLabel="Delete"
                    onConfirm={confirmDeleteChat}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}

            {shareTarget && chats.find((c) => c.id === shareTarget) && (
                <ShareChatDialog
                    chat={chats.find((c) => c.id === shareTarget)}
                    onClose={() => setShareTarget(null)}
                    onShareChange={setChatShareToken}
                />
            )}
        </div>
    );
}